import React, {
  ComponentType,
  useCallback,
  useEffect, useMemo,
  useState,
} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useStateContext} from '../../hooks/useStateContext';
import api from '../../services/api';
import {
  Collapse,
  IconButton,
  ListItemAvatar,
  ListItemIcon,
  Stack, Typography,
} from '@mui/material';
import {createNotification} from '../../reducer/actions/createNotification';
import Avatar from '@mui/material/Avatar';
import {styled, useTheme} from '@mui/material/styles';
import OnlinePredictionRoundedIcon from '@mui/icons-material/OnlinePredictionRounded';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import {auth} from '../../services/auth';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import List from '@mui/material/List';
import Popover from '@mui/material/Popover';
import AssistantIcon from '@mui/icons-material/Assistant';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import Box from '@mui/material/Box';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import Drawer from '@mui/material/Drawer';
import Badge from '@mui/material/Badge';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import {UserSettings} from '../../pages/General/UserSettings';
import toast from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import NotificationPopover from "./Notification/NotificationPopover";
import NotificationList from './Notification/NotificationList';
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {MenuWrapper} from "./Menu/MenuWrapper";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import {io} from "socket.io-client";
import {ReservationDialog} from "../Group/ReservationDialog/ReservationDialog";
import { ViewAs } from './ViewAs';
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";

interface Props {
  changePage: () => void;
}

export const LeftBar: ComponentType<Props> = ({changePage}) => {
  const navigate = useNavigate();

  useEffect(() => {
    changePage();
  }, [navigate]);

  const location = useLocation();

  const {
    user: {views},
  } = useStateContext();

  type OnlineUser = {
    id: number;
    name: string;
    avatar: string;
    avatar_color: string;
  };

  const {user} = useStateContext();
  const [collapsed, setCollapsed] = useState(true);
  const [users, setUsers] = useState<any>([]);
  const [newNotificationsCount, setNewNotificationsCount] = useState<number>(0);
  const [addingEntity, setAddingEntity] = useState<any>({
    schema: undefined,
  });
  const [invisible, setInvisible] = useState(false);
  const [notifyEl, setNotifyEl] = useState<HTMLButtonElement | null>(null);
  const [loadingOnlineUsers, setLoadingOnlineUsers] = useState(false);

  const fetchUsers = useCallback(() => {

    setLoadingOnlineUsers(true);

    api.get<OnlineUser[]>('/online-users').then(setUsers).finally(() => {
      setLoadingOnlineUsers(false);
    });
  }, []);

  function fetchNotifications() {
    api.get('/notifications').then((result: any) => {
      setNewNotificationsCount(result.new);
      if (result.show) {
        createNotification(`You have ${result.new} unread notification`, ``);
        toast(`You have ${result.new} unread notification`, {
          icon: '🔔',
        });
      }
    });
  }

  const handleClickToggle = () => {
    setCollapsed(!collapsed);
  };
  const handleBadgeVisibility = () => {
    setInvisible(!invisible);
  };
  const handleNotifyOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setNotifyEl(e.currentTarget);
    if (!invisible) {
      handleBadgeVisibility();
    }
  };
  const handleNotifyClose = () => {
    setNotifyEl(null);
  };

  const StyledBadge = styled(Badge)(({theme}) => ({
    '& .MuiBadge-badge': {
      backgroundColor: '#44b700',
      color: '#44b700',
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: 'ripple 1.2s infinite ease-in-out',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
  }));

  const [avatarEl, setAvatarEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [openOnlineUsers, setOpenOnlineUsers] = useState(false);
  const [sideBarBgColor, setSideBarBgColor] = useState('#256861');
  const avatarOpen = Boolean(avatarEl);
  const notifyOpen = Boolean(notifyEl);
  const [openAdminMenu, setOpenAdminMenu] = useState(false);

  const themeQuery = useTheme();
  const isMobile = useMediaQuery(themeQuery.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpenAdminMenu(newOpen);
  };

  const handleAvatarClick = (e: any) => {
    setAvatarEl(e.currentTarget);
  };

  const handleAvatarClose = () => {
    setAvatarEl(null);
  };

  const handleClick = () => {
    setOpenOnlineUsers(!openOnlineUsers);
    if (!openOnlineUsers) {
      fetchUsers();
    }
  };

  const handleSetAppBarColor = () => {
    let color = '#256861';
    if (window.location.href.includes('prod-zoe')) {
      color = '#033677';
    }
    if (window.location.href.includes('uat-zoe')) {
      color = '#598cd9';
    }
    setSideBarBgColor(color);
  };

  const [toggled, setToggled] = useState(false);
  const [hasImage, setHasImage] = useState(false);
  const [shake, setShake] = useState(false);
  const [openUserSettingsPage, setOpenUserSettingsPage] = useState<any>(false);
  const [userData, setUserData] = useState<any>(false);

  useEffect(() => {
    fetchUserData();
    fetchNotifications();
    handleSetAppBarColor();
  }, []);

  const fetchUserData = useCallback(() => {
    api.get(`/users/${user.id}`).then((res: any) => {
      setUserData(res);
    });
  }, []);

  useEffect(() => {
    const socket = io('https://socket-zoe.stagemporium.com');

    socket.on('notification', (data:any) => {
      if ( data?.notified_user_id == user?.id ) {
        fetchNotifications();
      }
    });
    socket.on('refresh-notifications', (data:any) => {
      if ( data?.notified_user_id == user?.id ) {
        fetchNotifications();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const [openedReservations, setOpenedReservations] = useState<any>(false);

  const memoizedNotificationList = useMemo(() => {
    return (
      <NotificationList
        openSidePane={(row: any) => {
          //setOpenedReservations(row);
        }}
        openReservations={(row: any) => {
          setOpenedReservations(row);
        }}
        refresh={() => {
          fetchNotifications();
        }}
      />
    );
  }, []);

  const isAdminOrTC = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]) || hasPermissionByPriority(user.roles, ROLE_PRIORITIES[100])|| hasPermissionByPriority(user.roles, ROLE_PRIORITIES[101]);

  return (
    <>

      {isMobile && (
        <Box sx={{flexGrow: 1}}>

          <AppBar
            position="static"
            sx={{
              background: `${sideBarBgColor}`
            }}
          >
            <Toolbar>
              <Typography variant="h6" component="div" sx={{flexGrow: 1}}>
                Zoe App
              </Typography>
              <IconButton
                onClick={toggleMobileMenu}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: "#fff",
                }}
              >
                <MenuRoundedIcon/>
              </IconButton>
            </Toolbar>

          </AppBar>
        </Box>
      )}

      {!isMobile && (
        <Box
          sx={{
            position: 'relative',
            '& .ps-menu-icon.ps-disabled': {
              color: 'rgb(0 0 0 / 25%)',
            },
          }}
        >
          <IconButton
            onClick={handleClickToggle}
            aria-label="delete"
            sx={{
              position: 'absolute',
              top: '10px',
              right: '-10px',
              zIndex: '10',
              color: '#fff',
              p: '2px',
              background: '#42948c',
              boxShadow: '0px 0px 0px 2px #fff',
              '&:hover': {
                background: '#2d7e75',
              },
            }}
            size="small"
          >
            {collapsed ? (
              <ChevronRightRoundedIcon fontSize={'inherit'}/>
            ) : (
              <ChevronLeftRoundedIcon fontSize={'inherit'}/>
            )}
          </IconButton>

          <MenuWrapper
            collapsed={collapsed}
            toggled={toggled}
            setToggled={setToggled}
            setBroken={setCollapsed}
            user={user}
            userData={userData}
            newNotificationsCount={newNotificationsCount}
            shake={shake}
            handleNotifyOpen={(event: any) => {
              handleNotifyOpen(event);
            }}
            handleAvatarClick={(event: any) => {
              handleAvatarClick(event);
            }}
            location={location}
            navigate={navigate}
            sideBarBgColor={sideBarBgColor}
            hasImage={hasImage}
            toggleMobileMenu={toggleMobileMenu}
          />
        </Box>
      )}

      {/* Mobil Drawer (kihúzható menü) */}
      <Drawer anchor="right" open={mobileOpen} onClose={toggleMobileMenu} sx={{
        '& .MuiPaper-root.MuiDrawer-paper': {
          background: `${sideBarBgColor}`
        }
      }}>
        <Box sx={{width: 250, background: `${sideBarBgColor}`, height: "100%", color: "#fff"}}>

          <MenuWrapper
            collapsed={false}
            toggled={toggled}
            setToggled={setToggled}
            setBroken={setCollapsed}
            user={user}
            userData={userData}
            newNotificationsCount={newNotificationsCount}
            shake={shake}
            handleNotifyOpen={(event: any) => {
              handleNotifyOpen(event);
            }}
            handleAvatarClick={(event: any) => {
              handleAvatarClick(event);
            }}
            location={location}
            navigate={navigate}
            sideBarBgColor={sideBarBgColor}
            hasImage={hasImage}
            toggleMobileMenu={toggleMobileMenu}
          />

        </Box>
      </Drawer>

      <Popover
        id={'userMenuId-4324342'}
        open={avatarOpen}
        anchorEl={avatarEl}
        onClose={handleAvatarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List
          disablePadding
          sx={{
            minWidth: '300px',
          }}
        >
          {user?.role !== 'service-provider' && (
            <ListItem disablePadding>
              <ListItemButton onClick={handleClick}>
                <ListItemIcon
                  sx={{
                    minWidth: '36px',
                  }}
                >
                  <OnlinePredictionRoundedIcon color={'primary'}/>
                </ListItemIcon>
                <ListItemText primary="Online Users"/>
                {openOnlineUsers ? <ExpandLess/> : <ExpandMore/>}
              </ListItemButton>
            </ListItem>
          )}
          <Collapse in={openOnlineUsers} timeout="auto" unmountOnExit>
            <Divider/>
            <List component="div" disablePadding>

              {loadingOnlineUsers ? (
                <Stack alignItems={'center'} justifyItems={'center'} padding={'10px'}>
                  <CircularProgress size={'small'}/>
                </Stack>
              ) : (
                <>
                  {users.map((onlineUser: any, index: number) => {
                    return (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                            variant="dot"
                          >
                            <Avatar
                              sx={{
                                bgcolor: onlineUser?.avatar_color
                                  ? onlineUser?.avatar_color
                                  : '#678fff',
                              }}
                              src={
                                onlineUser?.avatar
                                  ? onlineUser?.avatar?.link
                                  : undefined
                              }
                            >
                              {!onlineUser?.avatar &&
                                onlineUser.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </StyledBadge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={onlineUser.name}
                          secondary={onlineUser.id === user.id && '(you)'}
                        />
                      </ListItem>
                    );
                  })}
                </>
              )}


            </List>
          </Collapse>
          {/* <Divider />
            <ListItemButton
              onClick={() => {
                setOpenPageSettings(true);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: '36px',
                }}
              >
                <SettingsRoundedIcon color={'disabled'} />
              </ListItemIcon>
              <ListItemText primary="Page Settings" />
            </ListItemButton>*/}
          <Divider/>
          {/* <ListItem key={2} disablePadding>
              <ListItemButton onClick={() => setPasswordDialogOpen(true)}>
                <ListItemIcon
                  sx={{
                    minWidth: '36px',
                  }}
                >
                  <LockResetRoundedIcon />
                </ListItemIcon>
                <ListItemText primary="Change Password" />
              </ListItemButton>
            </ListItem>
            <Divider />*/}
          <ListItemButton
            onClick={() => {
              setOpenUserSettingsPage(true);
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: '36px',
              }}
            >
              <SettingsRoundedIcon color={'disabled'}/>
            </ListItemIcon>
            <ListItemText primary="Settings"/>
          </ListItemButton>
          <Divider />
          {isAdminOrTC && <ViewAs />}
          <Divider/>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                const a = auth.logOut();
                console.log(a);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: '36px',
                }}
              >
                <LogoutRoundedIcon color={'error'}/>
              </ListItemIcon>
              <ListItemText primary="Logout"/>
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <NotificationPopover id="notifyId-65437673" open={notifyOpen} anchorEl={notifyEl} onClose={handleNotifyClose}>
        {memoizedNotificationList}
      </NotificationPopover>

      {openAdminMenu && (
        <Drawer
          open={openAdminMenu}
          anchor={'right'}
          onClose={toggleDrawer(false)}
        >
          <Box
            sx={{width: 250}}
            role="presentation"
            onClick={toggleDrawer(false)}
          >
            <List>
              <ListItemButton
                onClick={() => {
                  navigate('/admin/user-management');
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: '36px',
                  }}
                >
                  <ManageAccountsRoundedIcon color={'primary'}/>
                </ListItemIcon>
                <ListItemText primary="User Management"/>
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  navigate('/admin/status-management');
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: '36px',
                  }}
                >
                  <AssistantIcon color={'primary'}/>
                </ListItemIcon>
                <ListItemText primary="Status Management"/>
              </ListItemButton>

              <ListItemButton
                onClick={() => {
                  navigate('/admin/check-management');
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: '36px',
                  }}
                >
                  <ChecklistRoundedIcon color={'primary'}/>
                </ListItemIcon>
                <ListItemText primary="Check Management"/>
              </ListItemButton>
            </List>
          </Box>
        </Drawer>
      )}

      {openUserSettingsPage && (
        <UserSettings
          onClose={() => {
            setOpenUserSettingsPage(false);
            fetchUserData();
          }}
        />
      )}

      {openedReservations && (
        <ReservationDialog
          row={openedReservations}
          handleClose={() => {
            setOpenedReservations(false);
          }}
        />
      )}
    </>
  );
};
